/**
 * Shop Management — one page for the whole Ministry Shop.
 * Products (five fixed categories, physical or digital), stock, orders and
 * settings including ministry pricing and payment methods.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api, { getErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  ArrowLeft, ExternalLink, Loader2, Package, Plus, Save, ShoppingBag, Trash2, Upload,
} from 'lucide-react';
import ShopCategoriesTab from '@/components/shop/ShopCategoriesTab';
import ShopWebsiteFeedTab from '@/components/shop/ShopWebsiteFeedTab';
import ShopBasketsTab from '@/components/shop/ShopBasketsTab';
import ShopImportDialog from '@/components/shop/ShopImportDialog';
import { MediaUpload } from '@/components/MediaUpload';

const EMPTY = {
  name: '', category: 'bibles', kind: 'physical', short_description: '', description: '',
  image_url: '', gallery: [], price: 0, sale_price: 0, member_price: 0,
  publisher_source: 'external', publisher_name: '', author: '', sku: '', isbn: '',
  tags: [], status: 'draft', is_featured: false, stock: 0, low_stock_level: 5,
  weight_grams: 0, dimensions: '', shipping_note: '', file_url: '', file_format: '',
  file_size_mb: 0, price_overrides: {}, rental_enabled: false, rental_price: 0,
  rental_days: 0, is_bestseller: false, bundle_items: [],
};

const money = (v, c = 'USD') => `${c === 'USD' ? '$' : `${c} `}${Number(v || 0).toFixed(2)}`;

const UploadButton = ({ label, accept, onDone, testId }) => {
  const [busy, setBusy] = useState(false);
  const send = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await api.post('/shop/admin/media', form,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      onDone(res.data);
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <label className="inline-flex">
      <input type="file" accept={accept} className="hidden" data-testid={testId}
             onChange={(e) => send(e.target.files?.[0])} />
      <Button asChild variant="outline" size="sm" disabled={busy}>
        <span>
          {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                : <Upload className="h-3.5 w-3.5 mr-1.5" />}{label}
        </span>
      </Button>
    </label>
  );
};

const ProductDialog = ({ open, onOpenChange, product, meta }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(product || EMPTY);
  React.useEffect(() => setForm(product || EMPTY), [product, open]);
  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const save = useMutation({
    mutationFn: async () => {
      const payload = { ...form };
      delete payload.id; delete payload.category_label; delete payload.your_price;
      delete payload.in_stock; delete payload.created_at; delete payload.updated_at;
      delete payload.currency; delete payload.list_price; delete payload.slug;
      delete payload.related; delete payload.is_member;
      delete payload.contents; delete payload.bundle_value;
      delete payload.bundle_saving; delete payload.in_stock;
      payload.bundle_items = (form.bundle_items || []).map(
        (i) => ({ product_id: i.product_id, qty: Math.max(1, Number(i.qty) || 1) }));
      return product?.id
        ? (await api.put(`/shop/admin/products/${product.id}`, payload)).data
        : (await api.post('/shop/admin/products', payload)).data;
    },
    onSuccess: () => {
      toast.success(product?.id ? 'Product updated' : 'Product added');
      queryClient.invalidateQueries({ queryKey: ['shop-admin-products'] });
      queryClient.invalidateQueries({ queryKey: ['shop-admin-dashboard'] });
      onOpenChange(false);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="shop-product-dialog">
        <DialogHeader>
          <DialogTitle>{product?.id ? 'Edit product' : 'Add a product'}</DialogTitle>
          <DialogDescription>
            Every product sits in one category and is either physical or digital.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 max-h-[68vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs">Name *</Label>
              <Input value={form.name} data-testid="product-name-input"
                     onChange={(e) => set('name', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Category *</Label>
              <Select value={form.category} onValueChange={(v) => set('category', v)}>
                <SelectTrigger data-testid="product-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(meta?.categories || []).map((c) => (
                    <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Type *</Label>
              <Select value={form.kind} onValueChange={(v) => set('kind', v)}>
                <SelectTrigger data-testid="product-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="physical">Physical — stock &amp; shipping</SelectItem>
                  <SelectItem value="digital">Digital — download after purchase</SelectItem>
                  <SelectItem value="bundle">Bundle — several products, one price</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Source *</Label>
              <Select value={form.publisher_source}
                      onValueChange={(v) => set('publisher_source', v)}>
                <SelectTrigger data-testid="product-source-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(meta?.sources || []).map((s) => (
                    <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Publisher / supplier name</Label>
              <Input value={form.publisher_name} data-testid="product-publisher-input"
                     onChange={(e) => set('publisher_name', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Author</Label>
              <Input value={form.author}
                     onChange={(e) => set('author', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">SKU</Label>
              <Input value={form.sku} onChange={(e) => set('sku', e.target.value)} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs">Short description</Label>
              <Input value={form.short_description}
                     onChange={(e) => set('short_description', e.target.value)} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs">Description</Label>
              <Textarea rows={4} value={form.description}
                        onChange={(e) => set('description', e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Price *</Label>
              <Input type="number" step="0.01" value={form.price} data-testid="product-price-input"
                     onChange={(e) => set('price', Number(e.target.value))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Sale price</Label>
              <Input type="number" step="0.01" value={form.sale_price}
                     onChange={(e) => set('sale_price', Number(e.target.value))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Member price</Label>
              <Input type="number" step="0.01" value={form.member_price}
                     data-testid="product-member-price-input"
                     onChange={(e) => set('member_price', Number(e.target.value))} />
              <p className="text-[11px] text-slate-400">Blank = use the category or default discount</p>
            </div>
          </div>

          {form.kind === 'physical' ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs">Stock</Label>
                <Input type="number" value={form.stock} data-testid="product-stock-input"
                       onChange={(e) => set('stock', Number(e.target.value))} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Low stock warning at</Label>
                <Input type="number" value={form.low_stock_level}
                       onChange={(e) => set('low_stock_level', Number(e.target.value))} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Weight (g)</Label>
                <Input type="number" value={form.weight_grams}
                       onChange={(e) => set('weight_grams', Number(e.target.value))} />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs">Size / dimensions</Label>
                <Input value={form.dimensions}
                       onChange={(e) => set('dimensions', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Shipping note</Label>
                <Input value={form.shipping_note}
                       onChange={(e) => set('shipping_note', e.target.value)} />
              </div>
            </div>
          ) : form.kind === 'bundle' ? (
            <div className="space-y-3 border border-slate-200 p-4"
                 data-testid="product-bundle-builder">
              <Label className="text-xs">What goes in the bundle</Label>
              {(form.bundle_items || []).length === 0 && (
                <p className="text-xs text-slate-400">
                  Nothing added yet — a bundle needs at least two products.
                </p>
              )}
              {(form.bundle_items || []).map((entry, index) => {
                const child = (meta?.products || []).find((p) => p.id === entry.product_id);
                return (
                  <div key={`${entry.product_id}-${index}`}
                       className="flex items-center gap-3 border-b border-slate-100 pb-2"
                       data-testid={`bundle-item-${entry.product_id}`}>
                    <span className="text-sm flex-1 truncate">
                      {child?.name || entry.product_id}
                    </span>
                    <Input type="number" min="1" value={entry.qty} className="w-20"
                           onChange={(e) => set('bundle_items', form.bundle_items.map(
                             (it, i) => (i === index
                               ? { ...it, qty: Math.max(1, Number(e.target.value)) } : it)))} />
                    <Button size="sm" variant="ghost"
                            data-testid={`bundle-remove-${entry.product_id}`}
                            onClick={() => set('bundle_items',
                              form.bundle_items.filter((_, i) => i !== index))}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                );
              })}
              <Select value="" onValueChange={(v) => {
                if ((form.bundle_items || []).some((i) => i.product_id === v)) return;
                set('bundle_items', [...(form.bundle_items || []),
                                     { product_id: v, qty: 1 }]);
              }}>
                <SelectTrigger data-testid="bundle-add-select">
                  <SelectValue placeholder="Add a product to the bundle" />
                </SelectTrigger>
                <SelectContent>
                  {(meta?.products || [])
                    .filter((p) => p.kind !== 'bundle' && p.id !== product?.id)
                    .map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} · {money(p.price, p.currency)}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-400">
                Bought separately these come to{' '}
                {money((form.bundle_items || []).reduce((sum, entry) => {
                  const child = (meta?.products || []).find((p) => p.id === entry.product_id);
                  return sum + (child ? child.price * entry.qty : 0);
                }, 0), meta?.currency)} — set the bundle price above to show the saving.
              </p>
            </div>
          ) : (
            <div className="space-y-3 border border-slate-200 p-4">
              <Label className="text-xs">The file buyers download</Label>
              <div className="flex flex-wrap items-center gap-3">
                <UploadButton label={form.file_url ? 'Replace file' : 'Upload the file'}
                              accept=".pdf,.epub,.mp3,.zip,.mp4,application/*"
                              testId="product-file-upload"
                              onDone={(d) => setForm((f) => ({
                                ...f, file_url: d.url, file_size_mb: d.size_mb,
                                file_format: (d.filename.split('.').pop() || '').toUpperCase(),
                              }))} />
                {form.file_url && (
                  <span className="text-xs text-emerald-700" data-testid="product-file-set">
                    {form.file_format} · {form.file_size_mb}MB
                  </span>
                )}
              </div>
              <Input value={form.file_url} className="hidden" readOnly data-testid="product-file-url-input" />
            </div>
          )}

          <div className="space-y-3 border border-slate-200 p-4">
            <Label className="text-xs">Picture</Label>
            <div className="flex flex-wrap items-center gap-3">
              {form.image_url && (
                <img src={form.image_url} alt="" data-testid="product-image-preview"
                     className="h-20 w-20 object-cover border border-slate-200" />
              )}
              <UploadButton label={form.image_url ? 'Replace picture' : 'Upload a picture'}
                            accept="image/*" testId="product-image-upload"
                            onDone={(d) => set('image_url', d.url)} />
            </div>
          </div>

          {form.kind === 'digital' && (
            <div className="space-y-3 border border-slate-200 p-4"
                 data-testid="product-rental-card">
              <div className="flex items-center gap-3">
                <Switch checked={!!form.rental_enabled} data-testid="product-rental-toggle"
                        onCheckedChange={(v) => set('rental_enabled', v)} />
                <span className="text-sm">Also let people rent this</span>
              </div>
              {form.rental_enabled && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Rental price *</Label>
                    <Input type="number" step="0.01" value={form.rental_price}
                           data-testid="product-rental-price-input"
                           onChange={(e) => set('rental_price', Number(e.target.value))} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Access for (days)</Label>
                    <Input type="number" value={form.rental_days}
                           data-testid="product-rental-days-input"
                           placeholder={String(meta?.rental_days || 7)}
                           onChange={(e) => set('rental_days', Number(e.target.value))} />
                    <p className="text-[11px] text-slate-400">
                      Blank or 0 uses the shop default ({meta?.rental_days || 7} days)
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {(meta?.currencies || []).length > 1 && (
            <div className="space-y-3 border border-slate-200 p-4"
                 data-testid="product-currency-prices">
              <Label className="text-xs">Prices in other currencies</Label>
              <p className="text-[11px] text-slate-400">
                Leave blank to convert automatically from{' '}
                {(meta?.currencies || [])[0]?.code}.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {(meta?.currencies || []).slice(1).map((c) => (
                  <div key={c.code} className="space-y-1.5">
                    <Label className="text-xs">{c.code}</Label>
                    <Input type="number" step="0.01"
                           data-testid={`product-price-${c.code}`}
                           value={(form.price_overrides || {})[c.code] ?? ''}
                           onChange={(e) => {
                             const next = { ...(form.price_overrides || {}) };
                             if (e.target.value === '') delete next[c.code];
                             else next[c.code] = Number(e.target.value);
                             set('price_overrides', next);
                           }} />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-6">
            <label className="flex items-center gap-3 text-sm">
              <Switch checked={form.status === 'published'} data-testid="product-publish-toggle"
                      onCheckedChange={(v) => set('status', v ? 'published' : 'draft')} />
              {form.status === 'published' ? 'Live in the shop' : 'Draft'}
            </label>
            <label className="flex items-center gap-3 text-sm">
              <Switch checked={!!form.is_featured} data-testid="product-featured-toggle"
                      onCheckedChange={(v) => set('is_featured', v)} />
              Featured
            </label>
            <label className="flex items-center gap-3 text-sm">
              <Switch checked={!!form.is_bestseller} data-testid="product-bestseller-toggle"
                      onCheckedChange={(v) => set('is_bestseller', v)} />
              Pin to the bestseller shelf
            </label>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending}
                  data-testid="product-save-btn">
            {save.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save product
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ProductsTab = () => {
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [category, setCategory] = useState('all');

  const { data, isLoading } = useQuery({
    queryKey: ['shop-admin-products', category],
    queryFn: async () => (await api.get(
      `/shop/admin/products${category === 'all' ? '' : `?category=${category}`}`)).data,
  });

  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/shop/admin/products/${id}`)).data,
    onSuccess: () => {
      toast.success('Product removed');
      queryClient.invalidateQueries({ queryKey: ['shop-admin-products'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (isLoading) return <Skeleton className="h-64 w-full" />;
  const products = data?.products || [];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-64" data-testid="shop-filter-category">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Every category</SelectItem>
            {(data?.categories || []).map((c) => (
              <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button className="ml-auto" variant="outline" data-testid="shop-import-btn"
                onClick={() => setImportOpen(true)}>
          <Upload className="h-4 w-4 mr-2" /> Import a spreadsheet
        </Button>
        <Button data-testid="shop-add-product-btn"
                onClick={() => { setEditing(null); setDialog(true); }}>
          <Plus className="h-4 w-4 mr-2" /> Add a product
        </Button>
      </div>

      {products.length === 0 ? (
        <Card><CardContent className="py-14 text-center" data-testid="shop-products-empty">
          <Package className="h-8 w-8 mx-auto text-slate-300" />
          <p className="mt-4 text-sm text-slate-500">
            Nothing in the catalogue yet — add your first product.
          </p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {products.map((p) => (
            <Card key={p.id} data-testid={`shop-admin-product-${p.id}`}>
              <CardContent className="py-4 flex flex-wrap items-center gap-4">
                {p.image_url
                  ? <img src={p.image_url} alt="" className="h-14 w-14 object-cover" />
                  : <div className="h-14 w-14 grid place-items-center bg-slate-50">
                      <Package className="h-4 w-4 text-slate-300" />
                    </div>}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-slate-900">{p.name}</span>
                    <Badge variant="outline" className="text-[10px]">{p.category_label}</Badge>
                    <Badge variant="secondary" className="text-[10px]">
                      {p.kind === 'digital' ? 'Digital' : 'Physical'}
                    </Badge>
                    {p.publisher_source === 'abmb' && (
                      <Badge variant="outline" className="text-[10px]">ABMB</Badge>
                    )}
                    <Badge variant="outline"
                           className={`text-[10px] ${p.status === 'published'
                             ? 'border-emerald-200 text-emerald-700' : 'text-slate-500'}`}>
                      {p.status}
                    </Badge>
                  </div>
                  <p className="mt-1 text-xs text-slate-500">
                    {money(p.price, p.currency)}
                    {p.member_price < p.price && ` · members ${money(p.member_price, p.currency)}`}
                    {p.kind === 'physical' && ` · ${p.stock} in stock`}
                    {p.sku && ` · ${p.sku}`}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" data-testid={`shop-edit-${p.id}`}
                          onClick={() => { setEditing(p); setDialog(true); }}>Edit</Button>
                  <Button size="sm" variant="ghost" data-testid={`shop-delete-${p.id}`}
                          onClick={() => remove.mutate(p.id)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <ProductDialog open={dialog} onOpenChange={setDialog} product={editing} meta={data} />
      <ShopImportDialog open={importOpen} onOpenChange={setImportOpen} />
    </div>
  );
};

const OrdersTab = () => {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState('open');
  const [channel, setChannel] = useState('all');

  const { data, isLoading } = useQuery({
    queryKey: ['shop-admin-orders', filter, channel],
    queryFn: async () => (await api.get(
      `/shop/admin/orders?status=${filter}${channel === 'all' ? '' : `&channel=${channel}`}`)).data,
  });

  const setStatus = useMutation({
    mutationFn: async ({ id, status }) => (await api.put(
      `/shop/admin/orders/${id}/status`, { status })).data,
    onSuccess: () => {
      toast.success('Order updated');
      queryClient.invalidateQueries({ queryKey: ['shop-admin-orders'] });
      queryClient.invalidateQueries({ queryKey: ['shop-admin-dashboard'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (isLoading) return <Skeleton className="h-64 w-full" />;
  const orders = data?.orders || [];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-2">
        {[['open', 'To handle'], ['unpaid', 'Unpaid'], ['shipped', 'Shipped'],
          ['delivered', 'Delivered'], ['cancelled', 'Cancelled']].map(([key, label]) => (
            <button key={key} onClick={() => setFilter(key)}
                    data-testid={`shop-orders-filter-${key}`}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full border
                      ${filter === key ? 'bg-slate-900 text-white border-slate-900'
                                       : 'bg-white text-slate-600 border-slate-200'}`}>
              {label} <span className="opacity-60">{data?.counts?.[key] ?? ''}</span>
            </button>
          ))}
        <Select value={channel} onValueChange={setChannel}>
          <SelectTrigger className="w-48 h-8 text-xs ml-auto"
                         data-testid="shop-orders-channel-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Every channel</SelectItem>
            {(data?.channel_options || []).map((c) => (
              <SelectItem key={c.key} value={c.key}>
                {c.label} ({data?.channels?.[c.key] ?? 0})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {orders.length === 0 ? (
        <Card><CardContent className="py-14 text-center" data-testid="shop-orders-empty">
          <ShoppingBag className="h-8 w-8 mx-auto text-slate-300" />
          <p className="mt-4 text-sm text-slate-500">No orders here yet.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {orders.map((o) => (
            <Card key={o.id} data-testid={`shop-order-${o.order_number}`}>
              <CardContent className="py-4 flex flex-wrap items-center gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-xs text-slate-400">{o.order_number}</span>
                    <span className="font-semibold">{o.customer?.name}</span>
                    <Badge variant="outline"
                           className={`text-[10px] ${o.payment_status === 'paid'
                             ? 'border-emerald-200 text-emerald-700'
                             : 'border-amber-200 text-amber-700'}`}>
                      {o.payment_status}
                    </Badge>
                    <Badge variant="secondary" className="text-[10px]">{o.status}</Badge>
                    <Badge variant="outline"
                           data-testid={`shop-order-channel-${o.order_number}`}
                           className={`text-[10px] ${o.channel === 'website'
                             ? 'border-[#B4881F] text-[#8a6716] bg-[#FBF3E2]'
                             : 'text-slate-500'}`}>
                      {o.channel === 'website' ? 'Website' : o.channel_label}
                    </Badge>
                    {o.is_member && <Badge variant="outline" className="text-[10px]">member</Badge>}
                    {o.gift?.is_gift && (
                      <Badge variant="outline"
                             data-testid={`shop-order-gift-${o.order_number}`}
                             className="text-[10px] border-[#B4881F]/50 text-[#8a6716]">
                        gift{o.gift.wrap ? ' · wrap' : ''}
                      </Badge>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-slate-500">
                    {o.customer?.email} · {(o.items || []).length} items ·{' '}
                    {money(o.total, o.currency)} · {o.payment_method}
                    {o.needs_shipping ? ' · needs shipping' : ''}
                    {o.has_digital ? ' · has downloads' : ''}
                  </p>
                  {o.gift?.is_gift && (
                    <div className="mt-2 border-l-2 border-[#B4881F] pl-3"
                         data-testid={`shop-order-gift-note-${o.order_number}`}>
                      <p className="text-xs text-slate-500">
                        Gift for {o.gift.recipient_name || 'a friend'}
                        {o.gift.recipient_email ? ` · ${o.gift.recipient_email}` : ''}
                        {o.gift.wrap ? ' · wrap with ribbon' : ''}
                        {o.gift.note_sent ? ' · note emailed' : ''}
                      </p>
                      {o.gift.note && (
                        <p className="mt-1 text-sm italic text-slate-700"
                           style={{ fontFamily: 'Georgia, serif' }}>
                          “{o.gift.note}”
                        </p>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {o.payment_status !== 'paid' && (
                    <Button size="sm" data-testid={`shop-order-markpaid-${o.order_number}`}
                            onClick={() => setStatus.mutate({ id: o.id, status: 'paid' })}>
                      Mark paid
                    </Button>
                  )}
                  {o.payment_status === 'paid' && o.needs_shipping && o.status !== 'shipped' && (
                    <Button size="sm" variant="outline"
                            data-testid={`shop-order-ship-${o.order_number}`}
                            onClick={() => setStatus.mutate({ id: o.id, status: 'shipped' })}>
                      Mark shipped
                    </Button>
                  )}
                  {o.status !== 'cancelled' && o.payment_status !== 'paid' && (
                    <Button size="sm" variant="ghost"
                            data-testid={`shop-order-cancel-${o.order_number}`}
                            onClick={() => setStatus.mutate({ id: o.id, status: 'cancelled' })}>
                      Cancel
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

const SettingsTab = () => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['shop-admin-settings'],
    queryFn: async () => (await api.get('/shop/admin/settings')).data,
  });
  const [form, setForm] = useState(null);
  React.useEffect(() => { if (data && !form) setForm(data); }, [data, form]);

  const save = useMutation({
    mutationFn: async () => (await api.put('/shop/admin/settings', {
      shop_name: form.shop_name, blurb: form.blurb, currency: form.currency,
      member_discount_percent: Number(form.member_discount_percent) || 0,
      category_discounts: form.category_discounts || {},
      shipping_flat_fee: Number(form.shipping_flat_fee) || 0,
      free_shipping_over: Number(form.free_shipping_over) || 0,
      shipping_note: form.shipping_note, payment_methods: form.payment_methods,
      bank_details: form.bank_details, paynow: form.paynow,
      low_stock_level: Number(form.low_stock_level) || 5,
      download_valid_days: Number(form.download_valid_days) || 30,
      notify_email: form.notify_email || '',
      email_notifications: form.email_notifications !== false,
      display_currencies: form.display_currencies || [],
      rental_days: Number(form.rental_days) || 7,
      gift_wrap_enabled: form.gift_wrap_enabled !== false,
      gift_wrap_fee: Number(form.gift_wrap_fee) || 0,
    })).data,
    onSuccess: (res) => {
      toast.success('Shop settings saved');
      setForm(res);
      queryClient.invalidateQueries({ queryKey: ['shop-admin-settings'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (isLoading || !form) return <Skeleton className="h-64 w-full" />;
  const set = (key, value) => setForm({ ...form, [key]: value });

  return (
    <div className="space-y-5" data-testid="shop-settings">
      <Card>
        <CardHeader><CardTitle className="text-base">Ministry pricing</CardTitle>
          <CardDescription>
            A product's own member price wins. Otherwise the category percentage, otherwise this default.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Default member discount (%)</Label>
            <Input type="number" value={form.member_discount_percent}
                   data-testid="shop-member-discount"
                   onChange={(e) => set('member_discount_percent', e.target.value)} />
          </div>
          {(form.categories || []).map((c) => (
            <div key={c.key} className="space-y-1.5">
              <Label className="text-xs">{c.label} (%)</Label>
              <Input type="number" data-testid={`shop-category-discount-${c.key}`}
                     value={form.category_discounts?.[c.key] ?? ''}
                     placeholder="use default"
                     onChange={(e) => set('category_discounts', {
                       ...(form.category_discounts || {}),
                       ...(e.target.value === '' ? {} : { [c.key]: Number(e.target.value) }),
                     })} />
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Currencies &amp; rentals</CardTitle>
          <CardDescription>
            Prices are held in the base currency; everything else converts live.
            Set a hand price per currency on any product to override the conversion.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Base currency</Label>
              <Select value={form.currency}
                      onValueChange={(v) => set('currency', v)}>
                <SelectTrigger data-testid="shop-base-currency"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {(form.available_currencies || []).map((c) => (
                    <SelectItem key={c.code} value={c.code}>
                      {c.code} — {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Gift wrap fee</Label>
              <Input type="number" step="0.01" value={form.gift_wrap_fee ?? 2.5}
                     data-testid="shop-gift-wrap-fee"
                     onChange={(e) => set('gift_wrap_fee', Number(e.target.value))} />
              <p className="text-[11px] text-slate-400">
                Charged once per order when a shopper asks for a ribbon
              </p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Rental access (days)</Label>
              <Input type="number" value={form.rental_days ?? 7}
                     data-testid="shop-rental-days"
                     onChange={(e) => set('rental_days', Number(e.target.value))} />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Shoppers can also see prices in</Label>
            <div className="flex flex-wrap gap-2" data-testid="shop-display-currencies">
              {(form.available_currencies || [])
                .filter((c) => c.code !== form.currency)
                .slice(0, 24)
                .map((c) => {
                  const on = (form.display_currencies || []).includes(c.code);
                  return (
                    <button key={c.code} data-testid={`shop-currency-toggle-${c.code}`}
                            onClick={() => set('display_currencies', on
                              ? form.display_currencies.filter((x) => x !== c.code)
                              : [...(form.display_currencies || []), c.code])}
                            className={`px-2.5 py-1 text-xs font-medium border rounded-full
                              ${on ? 'bg-slate-900 text-white border-slate-900'
                                   : 'bg-white text-slate-600 border-slate-200'}`}>
                      {c.code}
                    </button>
                  );
                })}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Order emails</CardTitle>
          <CardDescription>
            Buyers get a confirmation, you get an alert. Sent through the platform
            SMTP set up in System Settings → Email.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Send new-order alerts to</Label>
            <Input value={form.notify_email || ''} placeholder="shop@faithworldministries.com"
                   data-testid="shop-notify-email"
                   onChange={(e) => set('notify_email', e.target.value)} />
          </div>
          <div className="flex items-center gap-3 pt-6">
            <Switch checked={form.email_notifications !== false}
                    data-testid="shop-email-notifications-switch"
                    onCheckedChange={(v) => set('email_notifications', v)} />
            <span className="text-sm text-slate-600">Email on every new order</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Shop &amp; shipping</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Shop name</Label>
            <Input value={form.shop_name} data-testid="shop-name-input"
                   onChange={(e) => set('shop_name', e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Currency</Label>
            <Input value={form.currency} onChange={(e) => set('currency', e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Flat shipping fee</Label>
            <Input type="number" step="0.01" value={form.shipping_flat_fee}
                   data-testid="shop-shipping-fee"
                   onChange={(e) => set('shipping_flat_fee', e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Free shipping over</Label>
            <Input type="number" step="0.01" value={form.free_shipping_over}
                   onChange={(e) => set('free_shipping_over', e.target.value)} />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs">Shipping note shown to buyers</Label>
            <Input value={form.shipping_note}
                   onChange={(e) => set('shipping_note', e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Download link valid for (days)</Label>
            <Input type="number" value={form.download_valid_days}
                   onChange={(e) => set('download_valid_days', e.target.value)} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">How buyers pay</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {[['stripe', `Card (Stripe)${form.stripe_available ? '' : ' — no key configured'}`],
            ['paynow', 'EcoCash / OneMoney / InnBucks (Paynow)'],
            ['bank_transfer', 'Bank transfer, confirmed by hand']].map(([key, label]) => (
              <label key={key} className="flex items-center justify-between gap-4 text-sm">
                {label}
                <Switch checked={!!form.payment_methods?.[key]}
                        data-testid={`shop-pay-${key}`}
                        onCheckedChange={(v) => set('payment_methods',
                          { ...form.payment_methods, [key]: v })} />
              </label>
            ))}
          {form.payment_methods?.bank_transfer && (
            <div className="space-y-1.5">
              <Label className="text-xs">Bank details shown at checkout</Label>
              <Textarea rows={3} value={form.bank_details} data-testid="shop-bank-details"
                        onChange={(e) => set('bank_details', e.target.value)} />
            </div>
          )}
          {form.payment_methods?.paynow && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-slate-100 pt-4">
              <div className="space-y-1.5">
                <Label className="text-xs">Paynow integration id</Label>
                <Input value={form.paynow?.integration_id || ''} data-testid="shop-paynow-id"
                       onChange={(e) => set('paynow',
                         { ...form.paynow, integration_id: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">
                  Integration key {form.paynow?.integration_key_set ? '(saved)' : ''}
                </Label>
                <Input type="password" value={form.paynow?.integration_key || ''}
                       data-testid="shop-paynow-key" placeholder="leave blank to keep"
                       onChange={(e) => set('paynow',
                         { ...form.paynow, integration_key: e.target.value })} />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs">Wallets to offer</Label>
                <div className="flex flex-wrap gap-4">
                  {['ecocash', 'onemoney', 'innbucks'].map((w) => (
                    <label key={w} className="flex items-center gap-2 text-sm capitalize">
                      <input type="checkbox" data-testid={`shop-wallet-${w}`}
                             checked={(form.paynow?.enabled_methods || []).includes(w)}
                             onChange={(e) => {
                               const list = new Set(form.paynow?.enabled_methods || []);
                               if (e.target.checked) list.add(w); else list.delete(w);
                               set('paynow', { ...form.paynow, enabled_methods: [...list] });
                             }} />
                      {w}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Button onClick={() => save.mutate()} disabled={save.isPending}
              data-testid="shop-settings-save">
        {save.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        : <Save className="h-4 w-4 mr-2" />} Save settings
      </Button>
    </div>
  );
};

const Overview = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['shop-admin-dashboard'],
    queryFn: async () => (await api.get('/shop/admin/dashboard')).data,
  });
  if (isLoading) return <Skeleton className="h-40 w-full" />;
  const tiles = [
    ['Revenue', money(data?.revenue_total, data?.currency), 'shop-stat-revenue'],
    ['This month', money(data?.revenue_month, data?.currency), 'shop-stat-month'],
    ['Orders to handle', data?.orders_to_pack ?? 0, 'shop-stat-topack'],
    ['Unpaid orders', data?.orders_unpaid ?? 0, 'shop-stat-unpaid'],
    ['Products live', `${data?.products_published ?? 0}/${data?.products_total ?? 0}`, 'shop-stat-products'],
    ['Website orders to handle', data?.website_orders_open ?? 0, 'shop-stat-website-orders'],
  ];
  return (
    <div className="space-y-6" data-testid="shop-overview">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {tiles.map(([label, value, testId]) => (
          <Card key={label}><CardContent className="py-5">
            <p className="text-xs text-slate-500">{label}</p>
            <p className="mt-2 text-2xl font-semibold" data-testid={testId}>{value}</p>
          </CardContent></Card>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-base">Catalogue by category</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {(data?.by_category || []).map((c) => (
              <p key={c.key} className="flex justify-between text-sm">
                <span className="text-slate-600">{c.label}</span>
                <span className="font-medium">{c.published} live / {c.products}</span>
              </p>
            ))}
          </CardContent>
        </Card>
        <Card data-testid="shop-orders-by-channel">
          <CardHeader><CardTitle className="text-base">Where orders come from</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {(data?.orders_by_channel || []).map((c) => (
              <p key={c.key} className="flex justify-between text-sm">
                <span className="text-slate-600">{c.label}</span>
                <span className="font-medium">
                  {c.orders} orders · {money(c.revenue, data?.currency)}
                </span>
              </p>
            ))}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">Running low</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {(data?.low_stock || []).length === 0
              ? <p className="text-sm text-slate-500">Nothing is running low.</p>
              : data.low_stock.map((p) => (
                  <p key={p.id} className="flex justify-between text-sm">
                    <span className="text-slate-600">{p.name}</span>
                    <span className="font-medium text-amber-700">{p.stock} left</span>
                  </p>
                ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const ROW_FIELDS = {
  authors: [['name', 'Name'], ['bio', 'Short bio'], ['website', 'Website']],
  publishers: [['name', 'Name'], ['blurb', 'Blurb']],
  discounts: [['code', 'Code'], ['value', 'Value'], ['min_total', 'Minimum spend'], ['max_uses', 'Max uses'], ['expires_at', 'Expires (YYYY-MM-DD)']],
};

const ROW_MEDIA = {
  authors: ['photo_url', 'Photo', 'avatar'],
  publishers: ['logo_url', 'Logo', 'logo'],
};

const ExtrasList = ({ kind, rows, blank, label }) => {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState(blank);

  const save = useMutation({
    mutationFn: async (payload) => (payload.id
      ? (await api.put(`/shop/admin/${kind}/${payload.id}`, payload)).data
      : (await api.post(`/shop/admin/${kind}`, payload)).data),
    onSuccess: () => {
      toast.success(`${label} saved`);
      setDraft(blank);
      queryClient.invalidateQueries({ queryKey: ['shop-admin-extras'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/shop/admin/${kind}/${id}`)).data,
    onSuccess: () => {
      toast.success(`${label} removed`);
      queryClient.invalidateQueries({ queryKey: ['shop-admin-extras'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Card data-testid={`shop-${kind}-card`}>
      <CardHeader>
        <CardTitle className="text-base">{label}s</CardTitle>
        <CardDescription>
          {kind === 'discounts'
            ? 'Codes work in the shop and on the external site. Percentage unless you switch it to a fixed amount.'
            : `Shown on product pages and the ${kind === 'authors' ? 'author' : 'publisher'} listings.`}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {rows.length === 0 && (
          <p className="text-sm text-slate-500" data-testid={`shop-${kind}-empty`}>
            None yet.
          </p>
        )}
        {rows.map((r) => (
          <div key={r.id} className="flex flex-wrap items-center gap-3 border-b border-slate-100 pb-3"
               data-testid={`shop-${kind}-row-${r.id}`}>
            <span className="font-medium">{r.name || r.code}</span>
            <span className="text-xs text-slate-500">
              {kind === 'discounts'
                ? `${r.kind === 'percent' ? `${r.value}%` : money(r.value)} · used ${r.used}${r.max_uses ? `/${r.max_uses}` : ''}`
                : (r.bio || r.blurb || '')}
            </span>
            <Button size="sm" variant="ghost" className="ml-auto"
                    data-testid={`shop-${kind}-delete-${r.id}`}
                    onClick={() => remove.mutate(r.id)}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
          {ROW_FIELDS[kind].map(([key, fieldLabel]) => (
            <div key={key} className="space-y-1.5">
              <Label className="text-xs">{fieldLabel}</Label>
              <Input value={draft[key] ?? ''} data-testid={`shop-${kind}-${key}-input`}
                     onChange={(e) => setDraft({ ...draft, [key]: e.target.value })} />
            </div>
          ))}
          {ROW_MEDIA[kind] && (
            <div className="space-y-1.5">
              <MediaUpload value={draft[ROW_MEDIA[kind][0]] ?? ''}
                           onChange={(url) => setDraft({ ...draft, [ROW_MEDIA[kind][0]]: url })}
                           purpose={ROW_MEDIA[kind][2]} label={ROW_MEDIA[kind][1]}
                           testId={`shop-${kind}-${ROW_MEDIA[kind][0]}-input`}
                           previewClassName="h-16" />
            </div>
          )}
          {kind === 'discounts' && (
            <div className="space-y-1.5">
              <Label className="text-xs">Type</Label>
              <Select value={draft.kind} onValueChange={(v) => setDraft({ ...draft, kind: v })}>
                <SelectTrigger data-testid="shop-discounts-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">Percentage off</SelectItem>
                  <SelectItem value="fixed">Fixed amount off</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <Button size="sm" data-testid={`shop-${kind}-add`}
                onClick={() => save.mutate({
                  ...draft,
                  ...(kind === 'discounts' ? {
                    value: Number(draft.value) || 0,
                    min_total: Number(draft.min_total) || 0,
                    max_uses: Number(draft.max_uses) || 0,
                  } : {}),
                })}
                disabled={save.isPending || !(draft.name || draft.code)}>
          <Plus className="h-3.5 w-3.5 mr-1.5" /> Add {label.toLowerCase()}
        </Button>
      </CardContent>
    </Card>
  );
};

const ExtrasTab = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['shop-admin-extras'],
    queryFn: async () => (await api.get('/shop/admin/extras')).data,
  });
  if (isLoading) return <Skeleton className="h-64 w-full" />;
  return (
    <div className="space-y-5" data-testid="shop-extras">
      <ExtrasList kind="authors" label="Author" rows={data?.authors || []}
                  blank={{ name: '', bio: '', photo_url: '', website: '', is_featured: false }} />
      <ExtrasList kind="publishers" label="Publisher" rows={data?.publishers || []}
                  blank={{ name: '', blurb: '', logo_url: '', is_abmb: false }} />
      <ExtrasList kind="discounts" label="Discount code" rows={data?.discounts || []}
                  blank={{ code: '', kind: 'percent', value: 0, min_total: 0, max_uses: 0,
                           expires_at: '', is_active: true, note: '' }} />
    </div>
  );
};

const AdminShop = () => (
  <div className="min-h-screen bg-slate-50">
    <div className="mx-auto max-w-6xl px-6 py-10">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <Link to="/admin" className="text-xs text-slate-500 hover:text-slate-900
                                       inline-flex items-center gap-1.5">
            <ArrowLeft className="h-3.5 w-3.5" /> Admin
          </Link>
          <h1 className="mt-3 text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ShoppingBag className="h-6 w-6 text-[#B4881F]" /> Shop Management
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            One shop for Bibles, books, study guides, ministry resources and merchandise.
            Every sale posts to the Finance Ledger.
          </p>
        </div>
        <a href="/shop" target="_blank" rel="noreferrer">
          <Button variant="outline" data-testid="shop-view-public">
            <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> View the shop
          </Button>
        </a>
      </div>

      <Tabs defaultValue="overview" className="mt-8">
        <TabsList data-testid="shop-tabs">
          <TabsTrigger value="overview" data-testid="shop-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="products" data-testid="shop-tab-products">Products</TabsTrigger>
          <TabsTrigger value="categories" data-testid="shop-tab-categories">Categories</TabsTrigger>
          <TabsTrigger value="orders" data-testid="shop-tab-orders">Orders</TabsTrigger>
          <TabsTrigger value="website" data-testid="shop-tab-website">Website feed</TabsTrigger>
          <TabsTrigger value="baskets" data-testid="shop-tab-baskets">Baskets</TabsTrigger>
          <TabsTrigger value="extras" data-testid="shop-tab-extras">Authors &amp; codes</TabsTrigger>
          <TabsTrigger value="settings" data-testid="shop-tab-settings">Settings</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="mt-6"><Overview /></TabsContent>
        <TabsContent value="products" className="mt-6"><ProductsTab /></TabsContent>
        <TabsContent value="categories" className="mt-6"><ShopCategoriesTab /></TabsContent>
        <TabsContent value="orders" className="mt-6"><OrdersTab /></TabsContent>
        <TabsContent value="website" className="mt-6"><ShopWebsiteFeedTab /></TabsContent>
        <TabsContent value="baskets" className="mt-6"><ShopBasketsTab /></TabsContent>
        <TabsContent value="extras" className="mt-6"><ExtrasTab /></TabsContent>
        <TabsContent value="settings" className="mt-6"><SettingsTab /></TabsContent>
      </Tabs>
    </div>
  </div>
);

export default AdminShop;
